import { useCallback, useEffect, useMemo, useState } from 'react';
import contractsService from '../../../services/compliance/contracts.service';
import { AzureADUser } from '../../../services/permissions.service';
import { ContractNote } from '../../../types/compliance';

interface Props {
  contractId: string;
  adUsers: AzureADUser[];
  /** Pre-thread single-note field from compliance_contract.notes. Shown above
   *  the thread as a read-only legacy block when present. The migration
   *  backfills+clears populated values; this catches any stragglers. */
  legacyNote?: string | null;
}

function formatTimestamp(iso: string): { absolute: string; relative: string } {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  let relative: string;
  if (mins < 1) relative = 'just now';
  else if (mins < 60) relative = `${mins}m ago`;
  else if (mins < 60 * 24) relative = `${Math.round(mins / 60)}h ago`;
  else relative = `${Math.round(mins / (60 * 24))}d ago`;
  return { absolute: d.toLocaleString(), relative };
}

export default function ContractNotesPanel({ contractId, adUsers, legacyNote }: Props) {
  const [notes, setNotes] = useState<ContractNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const userNameByOid = useMemo(() => {
    const m = new Map<string, string>();
    adUsers.forEach((u) => m.set(u.user_id, u.display_name || u.email));
    return m;
  }, [adUsers]);

  const loadNotes = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await contractsService.listNotes(contractId);
      setNotes(rows);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleAdd = useCallback(async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const created = await contractsService.addNote(contractId, trimmed);
      setNotes((prev) => [...prev, created]);
      setBody('');
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [body, contractId]);

  // Newest first for the activity-log display.
  const sortedNotes = useMemo(
    () => [...notes].sort((a, b) => (a.created_at < b.created_at ? 1 : -1)),
    [notes],
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Add a note */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-panel">
        <label className="block text-sm font-medium text-label mb-1">
          Add a note
        </label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          placeholder="Leave a record of what you're observing. Notes are permanent — you can't overwrite a saved note."
          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-body"
          disabled={submitting}
          maxLength={5000}
        />
        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-gray-500">
            {body.length > 0 ? `${body.length} / 5000` : 'Notes are append-only and timestamped.'}
          </p>
          <button
            type="button"
            onClick={handleAdd}
            disabled={submitting || !body.trim()}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
          >
            {submitting ? 'Adding...' : 'Add Note'}
          </button>
        </div>
        {submitError && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-2">{submitError}</p>
        )}
      </div>

      {/* Legacy single-note field (pre-thread data) */}
      {legacyNote && legacyNote.trim() && (
        <div className="border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950 rounded-lg p-3">
          <div className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-1">
            Legacy note (pre-thread)
          </div>
          <div className="text-sm whitespace-pre-wrap text-amber-900 dark:text-amber-100">
            {legacyNote}
          </div>
        </div>
      )}

      {/* Thread */}
      {loading && (
        <div className="text-center text-gray-500 py-6 text-sm">Loading notes…</div>
      )}
      {loadError && (
        <div className="text-center text-red-600 dark:text-red-400 py-6 text-sm">
          Failed to load notes: {loadError}
        </div>
      )}
      {!loading && !loadError && sortedNotes.length === 0 && (
        <div className="text-center text-muted py-6 text-sm italic">
          No notes yet. Be the first to add one.
        </div>
      )}
      {!loading && sortedNotes.length > 0 && (
        <ul className="space-y-3">
          {sortedNotes.map((n) => {
            const ts = formatTimestamp(n.created_at);
            const author =
              userNameByOid.get(n.author_oid) || n.author_email || n.author_oid;
            return (
              <li
                key={n.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-panel"
              >
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-sm font-semibold text-body">
                    {author}
                  </span>
                  <span
                    className="text-xs text-muted"
                    title={ts.absolute}
                  >
                    {ts.relative}
                  </span>
                </div>
                <div className="text-sm whitespace-pre-wrap text-label">
                  {n.body}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
